<template>
  <div>
    <b-card class="visit-types-card">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 class="mb-0">
          Total Values of {{ getMonthName() }} - {{ formatCurrency(totalVisits) }}
        </h5>
      </div>
      
      <div class="row">
        <div class="col-lg-6 col-md-6">
          <div class="pie-chart-container">
            <vue-apex-charts
              :options="pieChartOptions"
              :series="pieSeries"
              type="pie"
              height="345"
            />
          </div>
        </div>
        
        <!-- Legend -->
        <div class="col-lg-6 col-md-6">
          <div class="legend-container">
            <div class="legend-item" v-for="(item, index) in visitTypes" :key="index">
              <div class="legend-color" :style="{ backgroundColor: colors[index] }"></div>
              <span class="legend-label">{{ item.label }}:</span>
              <span class="legend-count">{{ formatCurrency(item.count) }}</span>
            </div>
          </div>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";

export default {
  name: "VisitTypes",
  components: {
    "vue-apex-charts": VueApexCharts,
  },
  props: {
    yearData: {
      type: Array,
      default: () => []
    },
    userData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      colors: [
        '#FFCC99', // Más opaco - Follow Up
        '#FFB366', // Más opaco - New Consultation
        '#B8E6B8', // Más opaco - New Patient
        '#C8F7C8', // Más opaco - Pending Injection
        '#B8E0FF', // Más opaco - Pending Procedure
        '#E6CCFF', // Más opaco - Result
        '#F0F0F0', // Más opaco - COVID-19 Vaccination
        '#FFCCE6'  // Más opaco - Additional type if needed
      ]
    };
  },
  computed: {
    // Check if user has permission to view this component
    hasPermission() {
      const roleId = this.userData?.roles?.[0]?.id;
      return roleId === 1 || roleId === 2 || roleId === 3; // superadmin, admin, oma
    },
    
    visitTypes() {
      if (!this.yearData || this.yearData.length === 0) return [];
      
      // Get the most recent year data (usually the first one after combining)
      const latestData = this.yearData[0];
      if (!latestData || !latestData.totals) return [];
      
      const totals = latestData.totals;
      
      return [
        { label: 'Follow Up', key: 'F/U', count: totals['F/U'] || 0 },
        { label: 'New Consultation', key: 'N/C', count: totals['N/C'] || 0 },
        { label: 'New patient', key: 'N/P', count: totals['N/P'] || 0 },
        { label: 'Pending Injection', key: 'P/I', count: totals['P/I'] || 0 },
        { label: 'Pending Procedure', key: 'P/P', count: totals['P/P'] || 0 },
        { label: 'Result', key: 'R/S', count: totals['R/S'] || 0 },
        { label: 'COVID-19 Vaccination', key: 'V/C', count: totals['V/C'] || 0 },
        { label: 'M/B', key: 'M/B', count: totals['M/B'] || 0 }
      ].filter(item => item.count > 0); // Only show types with visits
    },
    
    pieSeries() {
      return this.visitTypes.map(item => item.count);
    },
    
    pieChartOptions() {
      return {
        chart: {
          type: 'pie',
          height: 400,
          toolbar: {
            show: false
          }
        },
        labels: this.visitTypes.map(item => item.label),
        colors: this.colors.slice(0, this.visitTypes.length),
        legend: {
          show: false // We'll use custom legend
        },
        dataLabels: {
          enabled: true,
          formatter: function (val, opts) {
            return val.toFixed(1) + "%"
          },
          style: {
            fontSize: '12px',
            fontWeight: 'bold',
            colors: ['#fff']
          }
        },
        tooltip: {
          y: {
            formatter: function (val, opts) {
              return "$" + val.toLocaleString('en-US')
            }
          }
        },
        plotOptions: {
          pie: {
            expandOnClick: true,
            donut: {
              size: '0%'
            }
          }
        },
        responsive: [{
          breakpoint: 768,
          options: {
            chart: {
              height: 400
            },
            dataLabels: {
              style: {
                fontSize: '10px'
              }
            }
          }
        }]
      };
    },
    
    totalVisits() {
      return this.visitTypes.reduce((total, item) => total + item.count, 0);
    }
  },
  methods: {
    formatCurrency(value) {
      // Formatear el valor con puntos como separadores de miles y signo de dólar
      return '$' + value.toLocaleString('en-US');
    },
    getMonthName() {
      if (!this.yearData || this.yearData.length === 0) return '';
      
      const latestData = this.yearData[0];
      if (!latestData || !latestData.month) return '';
      
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      
      return monthNames[latestData.month - 1] || '';
    }
  }
};
</script>

<style scoped>
.visit-types-card {
  border: none;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  margin-bottom: 20px;
}

.visit-types-card .card-body {
  padding: 20px;
}

.pie-chart-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.legend-container {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  padding: 10px 0;
  height: auto;
  margin-left: auto;
  max-width: fit-content;
}

.legend-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  width: auto;
  justify-content: flex-start;
}

.legend-color {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  margin-right: 8px;
  flex-shrink: 0;
}

.legend-label {
  font-weight: 500;
  color: #495057;
  margin-right: 8px;
  font-size: 0.9em;
}

.legend-count {
  font-weight: normal;
  color: #212529;
  background: none;
  padding: 0;
  border-radius: 0;
  font-size: 0.9em;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .legend-container {
    padding: 5px 0;
    align-items: center;
  }
  
  .legend-item {
    margin-bottom: 6px;
    padding: 2px 0;
    justify-content: center;
  }
  
  .legend-color {
    width: 18px;
    height: 18px;
    margin-right: 6px;
  }
  
  .legend-label {
    font-size: 0.8em;
  }
  
  .legend-count {
    font-size: 0.8em;
    background: none;
    padding: 0;
  }
}
</style>
